import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, type Transition } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import type { HeroImage } from '@/data/heroImageConfig';

type Variant = NonNullable<HeroImage['buttonVariant']>;

const BG_CLASS: Record<Variant, string> = {
    'solid-yellow': 'bg-specialYellow',
    'solid-white': 'bg-white',
    'solid-orange': 'bg-orange',
    'solid-creator': 'bg-gradient-to-r from-[#684FF8] to-[#6146FE] text-white bg-origin-border hover:scale-x-105 hover:from-green-700 hover:to-emerald-900 font-medium',
    'solid-mudpie': 'bg-gradient-to-r from-[#885836] to-[#A46F4A]',
    'solid-butter': 'bg-gradient-to-r from-yellow-300 to-amber-400',
    'solid-midnight': 'bg-gradient-to-r from-blue-800 to-indigo-600',
    'solid-red': 'bg-gradient-to-r from-[#C9291F] to-[#7D110B]',
    'border-creator': 'rounded-lg border-2 border-[#6146FE] bg-transparent',
    'border-white': 'rounded-lg border-2 border-white bg-transparent',
};

const TEXT_CLASS: Record<Variant, string> = {
    'solid-yellow': 'text-black',
    'solid-white': 'text-black',
    'solid-orange': 'text-white',
    'solid-creator': 'text-white',
    'solid-mudpie': 'text-white',
    'solid-butter': 'text-amber-900',
    'solid-midnight': 'text-white',
    'solid-red': 'text-white',
    'border-creator': 'text-[#6146FE]',
    'border-white': 'text-white',
};

// Background crossfades over the full carousel duration; width and label each
// take half of that, sequenced so the container always reaches its target
// before children change to fit.
const FULL_DURATION = 1.2;
const HALF_DURATION = FULL_DURATION / 2;
const EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];
const FULL_TR: Transition = { duration: FULL_DURATION, ease: EASE };
const HALF_TR: Transition = { duration: HALF_DURATION, ease: EASE };
const SWAP_TR: Transition = { duration: HALF_DURATION / 2, ease: EASE };

interface AnimatedPillProps {
    href: string;
    isExternal?: boolean;
    variant: Variant;
    label: string;
    showArrow?: boolean;
}

const AnimatedPill: React.FC<AnimatedPillProps> = ({ href, isExternal, variant, label, showArrow = true }) => {
    const navigate = useNavigate();

    // Hidden measurer mirrors the visible content (incl. padding + arrow)
    // so we can read the natural target width before committing to an animation.
    const measureRef = useRef<HTMLSpanElement>(null);
    const [width, setWidth] = useState<number | null>(null);
    const [displayedLabel, setDisplayedLabel] = useState(label);
    const pendingTimer = useRef<number | null>(null);

    // Width starts uncontrolled (null) so the button sizes naturally in the flow.
    // Once the first slide change fires, fonts are loaded and we switch to a
    // numeric width for animation. No timing dependency needed.

    // Sequence on label change: grow-then-swap if new is wider, swap-then-shrink
    // if narrower, plain crossfade if equal.
    useLayoutEffect(() => {
        if (label === displayedLabel) return;
        if (!measureRef.current) return;
        const newWidth = measureRef.current.offsetWidth;
        const currentWidth = width ?? newWidth;

        if (pendingTimer.current !== null) {
            window.clearTimeout(pendingTimer.current);
            pendingTimer.current = null;
        }

        if (newWidth > currentWidth + 0.5) {
            // Wider: grow the container first, then reveal new label once it fits.
            setWidth(newWidth);
            pendingTimer.current = window.setTimeout(() => {
                setDisplayedLabel(label);
                pendingTimer.current = null;
            }, HALF_DURATION * 1000);
        } else {
            // Same size or smaller: shrink container and swap label together.
            setWidth(newWidth);
            setDisplayedLabel(label);
        }
    }, [label, displayedLabel, width]);

    useEffect(() => () => {
        if (pendingTimer.current !== null) window.clearTimeout(pendingTimer.current);
    }, []);

    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isExternal) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        navigate(href);
    };

    const pillBaseClass = 'relative inline-flex items-center h-12 rounded-lg shadow-lg overflow-hidden will-change-transform';
    const innerClass = 'inline-flex items-center gap-2 whitespace-nowrap font-medium px-6';

    return (
        <motion.a
            href={href}
            onClick={onClick}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            initial={false}
            animate={width !== null ? { width } : undefined}
            transition={HALF_TR}
            whileHover={{ scaleX: 1.05 }}
            style={{ width: width ?? 'auto', transformOrigin: 'left center' }}
            className={pillBaseClass}
        >
            {/* Background layers — crossfade over the full carousel duration. */}
            <AnimatePresence initial={false}>
                <motion.span
                    key={variant}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={FULL_TR}
                    className={`absolute inset-0 ${BG_CLASS[variant]}`}
                    aria-hidden
                />
            </AnimatePresence>

            {/* Measurement node. When uncontrolled (width===null) it sits in the
                normal flow so the container sizes to it naturally — no font
                timing dependency. Once we have a numeric width it goes absolute
                so it no longer affects layout. */}
            <span
                ref={measureRef}
                aria-hidden
                className={`${innerClass} opacity-0 pointer-events-none ${width !== null ? 'absolute -z-10' : ''}`}
            >
                {label}
                {showArrow && <ArrowRight className="w-5 h-5" />}
            </span>

            {/* Visible label — always absolute so it overlays the measurement node. */}
            <span className="absolute inset-y-0 left-0 z-10 flex items-center">
                <AnimatePresence initial={false} mode="wait">
                    <motion.span
                        key={displayedLabel + '|' + variant}
                        initial={{ opacity: width === null ? 1 : 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={SWAP_TR}
                        className={`${innerClass} ${TEXT_CLASS[variant]}`}
                    >
                        {displayedLabel}
                        {showArrow && <ArrowRight className="w-5 h-5" />}
                    </motion.span>
                </AnimatePresence>
            </span>
        </motion.a>
    );
};

interface HeroCTAProps {
    region: string;
    image: HeroImage;
    className?: string;
}

const HeroCTA: React.FC<HeroCTAProps> = ({ region, image, className = '' }) => {
    const variant: Variant = image.buttonVariant || 'solid-yellow';
    const isExternal = !!image.externalLink;
    const primaryHref = isExternal
        ? (image.externalLink as string)
        : `/${region}/products/wired-headphones-1/${image.colorwayId ? `?variant=${image.colorwayId}` : ''}`;
    const primaryLabel = isExternal
        ? 'Learn More'
        : image.buttonText || 'Shop now';

    const secondaryHref = image.secondaryButtonHref
        || (image.secondaryButtonCode ? `/${region}/products/wired-headphones-1/?code=${image.secondaryButtonCode}` : null);
    const hasSecondary = !!(image.secondaryButtonText && secondaryHref);
    const secondaryVariant: Variant = image.secondaryButtonVariant || 'solid-white';

    return (
        <div className={`flex flex-wrap items-center gap-4 ${className}`}>
            <AnimatedPill
                href={primaryHref}
                isExternal={isExternal}
                variant={variant}
                label={primaryLabel}
                showArrow
            />

            <AnimatePresence initial={false}>
                {hasSecondary && (
                    <motion.div
                        key={secondaryHref}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={FULL_TR}
                    >
                        <AnimatedPill
                            href={secondaryHref as string}
                            isExternal={image.secondaryButtonExternal}
                            variant={secondaryVariant}
                            label={image.secondaryButtonText as string}
                            showArrow
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HeroCTA;
